import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { SearchService } from '../../services/search-service.service';
import { TranslateService } from '@ngx-translate/core';

const CAT_EVENTS: string = 'EVENT';

@Component({
  selector: 'app-whats-happening',
  templateUrl: './whats-happening.component.html',
  styleUrls: ['./whats-happening.component.scss'],
})
export class WhatsHappeningComponent implements OnInit, AfterViewInit, OnDestroy {
  events: any[] = [];
  isLoading: boolean = true;
  language: string = 'en';
  carouselInitialized: boolean = false;
  currentIndex: number = 0;
  currentPage: number = 1;
  pageSize: number = 4;
  initialLoadSize: number = 8;
  totalAvailable: number = 0;
  isLoadingMore: boolean = false;
  hasMoreData: boolean = true;

  constructor(
    private searchService: SearchService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef
  ) {}

  getVisibleCount(): number {
    const width = window.innerWidth;
    if (width < 768) return 1; // Mobile
    if (width >= 768 && width <= 1024) return 3; // Tablet
    return 4; // Desktop
  }

  ngOnInit(): void {
    this.language = localStorage.getItem('language') ?? 'en';
    this.loadEvents();
  }

  ngAfterViewInit(): void {
    if (this.events.length > 0 && !this.isLoading) {
      this.carouselInitialized = true;
      this.cdr.detectChanges();
    }
  }

  ngOnDestroy(): void {}

  nextSlide(): void {
    const visibleCount = this.getVisibleCount();
    const maxIndex = Math.max(0, this.events.length - visibleCount);

    if (this.currentIndex >= maxIndex - 2 && this.hasMoreData && !this.isLoadingMore) {
      this.loadMoreEvents();
    }

    if (this.currentIndex < maxIndex) {
      this.currentIndex++;
    }
  }

  canGoNext(): boolean {
    const visibleCount = this.getVisibleCount();
    const maxIndex = Math.max(0, this.events.length - visibleCount);
    return this.currentIndex < maxIndex || (this.hasMoreData && !this.isLoadingMore);
  }

  canGoPrev(): boolean {
    return this.currentIndex > 0;
  }

  prevSlide(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    }
  }

  getSlideClass(index: number): string {
    const visibleCount = this.getVisibleCount();
    const startIndex = this.currentIndex;
    const endIndex = this.currentIndex + visibleCount;

    if (index >= startIndex && index < endIndex) {
      const relativeIndex = index - startIndex;

      if (visibleCount === 4 && relativeIndex === 2) {
        return 'slide-visible slide-center';
      }

      if (visibleCount === 3 && relativeIndex === 1) {
        return 'slide-visible slide-center';
      }

      return 'slide-visible';
    }

    return 'slide-hidden';
  }

  getTransformStyle(): string {
    const offset = this.currentIndex * 210;
    return `translateX(-${offset}px)`;
  }

  loadEvents(): void {
    this.isLoading = true;
    this.currentPage = 1;
    this.hasMoreData = true;

    this.searchService
      .planYourVisitSearchListing(
        CAT_EVENTS, // category
        null, // subCategory
        null, // classifications
        null, // location
        null, // tag
        1 // page number
      )
      .subscribe({
        next: (data) => {
          if (data && data.numberOfResults) {
            this.totalAvailable = Math.min(data.numberOfResults, 20); // Cap at 20 events max
          }

          if (!data || !data.products || data.products.length === 0) {
            this.hasMoreData = false;
          } else if (data.products.length < this.initialLoadSize) {
            this.hasMoreData = false;
          }

          this.processEventResults(data, false);
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading events:', error);
          this.isLoading = false;
          this.hasMoreData = false;
        },
      });
  }

  loadMoreEvents(): void {
    if (this.isLoadingMore || !this.hasMoreData) {
      return;
    }

    this.isLoadingMore = true;
    this.currentPage++;

    this.searchService
      .planYourVisitSearchListing(
        CAT_EVENTS, // category
        null, // subCategory
        null, // classifications
        null, // location
        null, // tag
        this.currentPage // page number
      )
      .subscribe({
        next: (data) => {
          if (!data || !data.products || data.products.length === 0) {
            this.hasMoreData = false;
          } else if (data.products.length < this.pageSize) {
            this.hasMoreData = false;
          }

          this.processEventResults(data, true);
          this.isLoadingMore = false;
        },
        error: (error) => {
          console.error('Error loading more events:', error);
          this.isLoadingMore = false;
          this.hasMoreData = false;
          this.currentPage--; // Revert page increment on error
        },
      });
  }

  processEventResults(data: any, append: boolean = false): void {
    if (!data || !data.products) {
      return;
    }

    const itemsToLoad = append ? this.pageSize : this.initialLoadSize;
    const products = data.products.slice(0, itemsToLoad);

    const newEvents = products.map((event: any) => ({
      productId: event.productId,
      productName: event.productName || 'Event',
      productImage: this.processImageUrl(event.productImage),
      startDate: 'none',
      endDate: 'none',
      startRm: '',
      endRm: '',
      description: '',
      location: '',
      eventType: 'Event',
    }));

    if (append) {
      const startIndex = this.events.length;
      this.events = [...this.events, ...newEvents];

      products.forEach((event: any, index: number) => {
        if (!event || !event.productId) return;
        this.loadEventDetails(event, startIndex + index);
      });
    } else {
      this.events = newEvents;

      products.forEach((event: any, index: number) => {
        if (!event || !event.productId) return;
        this.loadEventDetails(event, index);
      });
    }

    this.cdr.detectChanges();
  }

  loadEventDetails(event: any, index: number): void {
    this.searchService.getViewListingDetailsEn(event.productId).subscribe({
      next: (detailData) => {
        if (!detailData) return;

        let startDate: any = 'none';
        let endDate: any = 'none';
        let startRm = '';
        let endRm = '';

        if (detailData.frequencyStartDate && detailData.frequencyStartDate != '') {
          const sDate = new Date(detailData.frequencyStartDate + 'T00:00:00');
          const startDateNum = sDate.getDate();
          startRm = ['st', 'nd', 'rd'][(startDateNum % 10) - 1] || 'th';
          startDate = sDate;

          if (detailData.frequencyStartDate == detailData.frequencyEndDate) {
            endDate = 'none';
          } else {
            const eDate = new Date(detailData.frequencyEndDate + 'T00:00:00');
            const endDateNum = eDate.getDate();
            endRm = ['st', 'nd', 'rd'][(endDateNum % 10) - 1] || 'th';
            endDate = eDate;
          }
        }

        this.events[index] = {
          ...this.events[index],
          productName: detailData.productName || event.productName || 'Event',
          startDate: startDate,
          endDate: endDate,
          startRm: startRm,
          endRm: endRm,
          description: detailData.productDescription || '',
          location: this.getEventLocation(detailData),
          eventType: this.getEventType(detailData),
        };

        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error(`Error loading details for event ${event.productId}:`, error);
      },
    });
  }

  processImageUrl(imageUrl: string): string {
    if (!imageUrl) return '/assets/images/default-event.jpg';

    if (typeof imageUrl === 'string') {
      try {
        let parts = imageUrl.split('&');
        if (parts.length >= 3) {
          parts[1] = 'fit=crop&w=350';
          parts[2] = 'h=450';
          return parts.join('&');
        } else if (parts.length === 1 && !imageUrl.includes('?')) {
          return imageUrl + '?fit=crop&w=350&h=450';
        }
      } catch (e) {}
    }
    return imageUrl;
  }

  parseEventDate(dateString: string): Date | null {
    if (!dateString || dateString === 'none') return null;
    try {
      return new Date(dateString);
    } catch {
      return null;
    }
  }

  getEventLocation(detailData: any): string {
    if (detailData.addresses && detailData.addresses.length > 0) {
      const addr = detailData.addresses[0];
      return addr.islandArea || addr.island || '';
    }
    return '';
  }

  getEventType(detailData: any): string {
    if (detailData.classification && detailData.classification.length > 0) {
      return detailData.classification[0];
    }
    return 'Event';
  }

  formatDateRange(startDate: Date | null, endDate: Date | null): string {
    if (!startDate) return '';

    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const start = startDate.toLocaleDateString('en-US', options).toUpperCase();

    if (endDate && endDate.getTime() !== startDate.getTime()) {
      const end = endDate.toLocaleDateString('en-US', options).toUpperCase();
      return `${start} - ${end}`;
    }

    return start;
  }

  getDateParts(date: Date | null): { month: string; day: string } {
    if (!date) return { month: '', day: '' };

    const month = date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase();
    const day = date.getDate().toString();

    return { month, day };
  }

  getDaySuffix(day: number): string {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  }

  navigateToEvent(eventId: string): void {
    if (eventId) {
      window.open(`/listingdetails/${eventId}`, '_blank');
    }
  }

  viewAllEvents(): void {
    window.open('/experience/events', '_blank');
  }
}
